import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dynVariadic } from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  instrumentid,
  internalteacherid,
  lessongroupid,
  studentid,
  waitinglistlessongroupid,
  weekday,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { renderDeleteButton } from "../../client/savebutton";
import { formulaType, parseWeekday } from "../../parsing_from_ur";
import {
  ddMMMMyyyy,
  ddMMyyyy,
  renderFirstNameLastName,
  renderFormulaType,
  renderFormulaTypeShort,
  renderTeacherPlusTimePlusWeekday,
} from "../../render";
import * as routes from "../../routes";
import { renderTableHeaderCellWithSorting } from "../payments_overview_non_periodic/client";

type item = {
  id: waitinglistlessongroupid;
  createdon: joda.Instant;
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
  };
  instrument: {
    id: instrumentid;
    description: string;
  };
  teacher: {
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  lessongroupid: lessongroupid;
  type: formulaType;
  firstlesson: joda.LocalDate | null;
  lastlesson: joda.LocalDate | null;
  time: joda.LocalTime;
  weekday: weekday;
};

type possibleSorting = "student" | "lessongroup" | "createdon";

export function render(opts: { items: item[] }): HTMLElement | HTMLElement[] {
  const itemsS = new Source(opts.items); // needs to be source so we can delete

  const tableid = "classy-table-id";

  const downloadLinks = renderTableDownloadLinks({
    tableid,
    filename: "Wachtlijst groepslessen & kampen",
  });

  const sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }> = new Source({ sorting: "student", direction: "asc" });

  function handleDelete(id: waitinglistlessongroupid) {
    itemsS.set(itemsS.get().filter((i) => i.id !== id));
  }

  return (
    <div>
      {downloadLinks}
      <table id={tableid}>
        {dynVariadic([itemsS, sortingS], ([items, sorting]) => {
          return [
            renderHeaderRow(sorting, sortingS),
            ...items
              .orderBy((i) => {
                return sorting.sorting === "student"
                  ? renderFirstNameLastName(i.student)
                  : sorting.sorting === "lessongroup"
                  ? makeDescription(i)
                  : sorting.sorting === "createdon"
                  ? i.createdon.toEpochMilli()
                  : checkAllCasesHandled(sorting.sorting);
              }, sorting.direction)
              .map((i) => renderRow(i, handleDelete)),
          ];
        })}
      </table>
    </div>
  );
}

function renderHeaderRow(
  currentSorting: {
    sorting: possibleSorting;
    direction: "asc" | "desc";
  },
  sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }>,
) {
  function renderHeaderCell(label: string, sorting: possibleSorting | null) {
    return renderTableHeaderCellWithSorting(
      label,
      sorting,
      currentSorting,
      sortingS,
    );
  }
  return (
    <tr style="position: sticky; top: 0px; background-color: white;">
      {renderHeaderCell("Student", "student")}
      {renderHeaderCell("Lessenreeks", "lessongroup")}
      {renderHeaderCell("Aangemaakt op", "createdon")}
      <th></th>
    </tr>
  );
}

function renderRow(
  item: item,
  handleDelete: (id: waitinglistlessongroupid) => void,
) {
  return (
    <tr>
      <td>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: item.student.id,
          })}
        >
          {renderFirstNameLastName(item.student)}
        </a>
      </td>
      <td>
        <a
          href={routes.lessongroup_generalRoute.link({
            lessongroupid: item.lessongroupid,
          })}
          style="white-space: pre;"
        >
          {makeDescription(item)}
        </a>
      </td>
      <td>
        {item.createdon.atZone(joda.ZoneId.UTC).toLocalDate().format(ddMMyyyy)}
      </td>
      <td>
        {renderDeleteButton({
          label: <i className={FA.icon_trash_alt_solid}></i>,
          className: style.button_has_only_icon,
          confirmationLabel: "Ben je zeker dat je dit van de wachtlijst wilt verwijderen?",
          dontShowLabelAgainInConfirmation: true,
          doDelete: async () => {
            await rpc(routes.deleteFromWaitinglistLessongroupSpec, undefined, {
              id: item.id,
            });
            handleDelete(item.id);
            return null;
          },
          redirectTo: "",
          panelStyle: `
          position: absolute;
          top: 48px;
          right: 0px;
          z-index: 1000;
          box-shadow: var(--box-shadow-large);`,
        })}
      </td>
    </tr>
  );
}

function makeDescription(m: item): string {
  return (
    renderFormulaTypeShort(m.type) +
    " " +
    renderTeacherPlusTimePlusWeekday(
      m.teacher,
      m.time,
      parseWeekday(m.weekday),
    ) +
    (m.firstlesson && m.lastlesson
      ? "\n" + ddMMMMyyyy(m.firstlesson) + " - " + ddMMMMyyyy(m.lastlesson)
      : "")
  );
}
